import createClient from 'openapi-fetch';
import type { components, paths } from './schema';

export type Schemas = components['schemas'];
export const API_BASE = '/api/v1';

/** Error body of every non-2xx response (API/CONVENTIONS.md). */
export interface ApiErrorBody {
  error: {
    code: string;
    message: string;
    details?: { path: string; message: string }[];
    requestId?: string;
  };
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details: { path: string; message: string }[] = [],
    readonly requestId?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  static fromBody(response: Response, body: ApiErrorBody | null | undefined): ApiError {
    return new ApiError(
      response.status,
      body?.error.code ?? `HTTP_${String(response.status)}`,
      body?.error.message ?? response.statusText,
      body?.error.details ?? [],
      body?.error.requestId ?? response.headers.get('x-request-id') ?? undefined,
    );
  }

  static async from(response: Response): Promise<ApiError> {
    const body = (await response.json().catch(() => null)) as ApiErrorBody | null;
    return ApiError.fromBody(response, body);
  }
}

// --- session plumbing (set by the auth provider) ---------------------------------------

interface SessionHooks {
  accessToken: () => string | null;
  /** Gets a new access token with the refresh token; null when the session is over. */
  refresh: () => Promise<string | null>;
  onSessionExpired: () => void;
  /** A platform route answered 403 MFA_SETUP_REQUIRED: the admin must enrol first. */
  onMfaSetupRequired?: () => void;
}

let hooks: SessionHooks = {
  accessToken: () => null,
  refresh: () => Promise.resolve(null),
  onSessionExpired: () => undefined,
};

export function bindSession(next: SessionHooks): void {
  hooks = next;
}

let refreshing: Promise<string | null> | null = null;

/** Runs one refresh at a time; concurrent 401s share it. */
function refreshOnce(): Promise<string | null> {
  refreshing ??= hooks.refresh().finally(() => {
    refreshing = null;
  });
  return refreshing;
}

/**
 * fetch with the platform session: bearer token, an Idempotency-Key on every POST, one shared
 * refresh + retry on 401 (never for `/platform/auth/*`), and the MFA-setup signal on 403.
 */
export async function authFetch(input: Request): Promise<Response> {
  const retry = input.clone();
  const send = (req: Request, token: string | null) => {
    if (token) req.headers.set('Authorization', `Bearer ${token}`);
    if (req.method === 'POST' && !req.headers.has('Idempotency-Key')) {
      req.headers.set('Idempotency-Key', crypto.randomUUID());
    }
    return fetch(req);
  };
  const watch = async (response: Response) => {
    if (response.status === 403 && hooks.onMfaSetupRequired) {
      const body = (await response
        .clone()
        .json()
        .catch(() => null)) as ApiErrorBody | null;
      if (body?.error.code === 'MFA_SETUP_REQUIRED') hooks.onMfaSetupRequired();
    }
    return response;
  };
  const response = await send(input, hooks.accessToken());
  if (response.status !== 401 || input.url.includes('/auth/')) return watch(response);
  const token = await refreshOnce();
  if (!token) {
    hooks.onSessionExpired();
    return response;
  }
  return watch(await send(retry, token));
}

/** Typed client for every path in the OpenAPI document (`/platform/...`). */
export const api = createClient<paths>({
  baseUrl: new URL(API_BASE, globalThis.location.origin).href,
  fetch: authFetch,
});

/** Unwraps an openapi-fetch result: returns data or throws ApiError. */
export async function unwrap<T>(
  promise: Promise<{ data?: T; error?: unknown; response: Response }>,
): Promise<T> {
  const { data, error, response } = await promise;
  if (error !== undefined || !response.ok) {
    throw ApiError.fromBody(response, error as ApiErrorBody | undefined);
  }
  return data as T;
}

type Query = Record<string, string | number | boolean | null | undefined>;

/**
 * Untyped request through the same session plumbing, for endpoints not yet in the generated schema.
 * `path` is relative to /api/v1 (e.g. `/platform/dashboard`). Returns undefined for 204.
 */
export async function request<T>(
  method: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE',
  path: string,
  options: { body?: unknown; query?: Query; headers?: Record<string, string> } = {},
): Promise<T> {
  const url = new URL(`${API_BASE}${path}`, globalThis.location.origin);
  for (const [k, v] of Object.entries(options.query ?? {})) {
    if (v !== undefined && v !== null && v !== '') url.searchParams.set(k, String(v));
  }
  const headers = new Headers(options.headers);
  if (options.body !== undefined) headers.set('Content-Type', 'application/json');
  const response = await authFetch(
    new Request(url, {
      method,
      headers,
      body: options.body === undefined ? undefined : JSON.stringify(options.body),
    }),
  );
  if (!response.ok) throw await ApiError.from(response);
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}
